/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MYBROADCASTS_PHASE2_REFRESH_CALLBACK
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Add refreshCallback prop and pass to DashboardBroadcastSummaryTable
 * for resend functionality refresh.
 *
 * File Size Check:
 * - Current: 106 lines
 * - Adding: ~5 lines
 * - Projected: 111 lines
 * - Decision: Proceed
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/DashboardBroadcastSummaryTable.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ALLBROADCASTS_SIDEBAR_FILTER
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Updated to accept totalItems and totalPages as props for better integration
 * with filtered results in sidebar layout.
 *
 * File Size Check:
 * - Current: 87 lines
 * - Adding: ~5 lines
 * - Projected: 92 lines
 * - Decision: Proceed (within limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastsTabContent.tsx
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastsTabs.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCASTS_FILTER_INTEGRATION
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Updated for filter compatibility. Handled filtered item counts for pagination.
 *
 * File Size Check:
 * - Current: 82 lines
 * - Adding: ~5 lines
 * - Projected: 87 lines
 * - Decision: Proceed (within limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastsTabContent.tsx
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastsTabs.tsx
 */

import React from "react";
import { Broadcast } from "../../../../../interfaces/broadcast";
import DashboardBroadcastSummaryTable from "../../../../dashboard/DashboardBroadcastSummaryTable";
import { PaginationControls } from "../../../../../components/ui/PaginationControls";

interface BroadcastsTabContentProps {
  data: Broadcast[];
  loading: boolean;
  error: string | null;
  tabKey: "recent" | "wts" | "wtb" | "mine";
  currentPage: number;
  itemsPerPage: number;
  totalItems: number; // [2026-01-01] NEW - for filtered count
  totalPages: number; // [2026-01-01] NEW - calculated from filtered results
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (limit: number) => void;
  loadingMessage?: string;
  refreshCallback?: () => void; // [2025-01-02] Phase 2: For resend refresh
  showMobileFooter?: boolean;   // [2026-02-10] Show company name in mobile footer
  showMobileActions?: boolean;  // [2026-02-10] Show Edit/Delete in mobile footer
}

export const BroadcastsTabContent: React.FC<BroadcastsTabContentProps> = ({
  data,
  loading,
  error,
  tabKey,
  currentPage,
  itemsPerPage,
  totalItems,
  totalPages,
  handlePageChange,
  handleItemsPerPageChange,
  loadingMessage = "Loading broadcasts...",
  refreshCallback, // [2025-01-02] Phase 2: Destructure refresh callback
  showMobileFooter,   // [2026-02-10] Mobile footer props
  showMobileActions,  // [2026-02-10] Mobile actions props
}) => {
  if (loading) {
    return <div className="py-4 text-center">{loadingMessage}</div>;
  }

  if (error) {
    return <div className="py-4 text-center text-danger">{error}</div>;
  }

  return (
    <>
      <DashboardBroadcastSummaryTable
        broadcasts={data}
        currentTab={tabKey}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        refreshCallback={refreshCallback} // [2025-01-02] Phase 2: Pass to table
        showMobileFooter={showMobileFooter}
        showMobileActions={showMobileActions}
      />
      {/* [2026-01-01] Pagination controls using passed props */}
      {totalPages > 0 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          loading={loading}
        />
      )}
    </>
  );
};
